import { createConsumer, type Subscription } from "@rails/actioncable";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { createPortal } from "react-dom";
import TextareaAutosize from "react-textarea-autosize";
import { LoaderCircle } from "lucide-react";
import Message from "./message";
import { buttonClasses } from "./utils";

type Chat = {
  id: number;
};

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

interface DrawerProps {
  minRows?: number;
  value?: string;
  className: string;
  name: string;
  onSelectAIText?: (text: string) => void;
  onClose: (e: any) => void;
}

const DrawerPortal = ({
  className,
  name,
  onSelectAIText = () => {},
  onClose,
}: DrawerProps) => {
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [currentCompletion, setCurrentCompletion] = useState<ChatMessage | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentAiText, setCurrentAiText] = useState("");
  const [isLocalOpen, setIsLocalOpen] = useState(false);
  const messagesRef = useRef<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputAiTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const portalRoot = useRef<HTMLDivElement | null>(null);
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);

  function persistMessages(nextMessages: ChatMessage[]) {
    messagesRef.current = nextMessages;
    localStorage.setItem("chatMessages", JSON.stringify(nextMessages));
    setMessages(nextMessages);
  }

  function normalizedFieldName() {
    const bracketMatch = name?.match(/\[([^\]]+)\]$/);
    if (bracketMatch) return bracketMatch[1];

    return name?.split(".").pop() || name;
  }

  function inferMode() {
    if (
      window.location.pathname.startsWith("/system/posts") &&
      ["title", "introduction", "content", "seo_description"].includes(
        normalizedFieldName(),
      )
    ) {
      return "post";
    }

    return "generic";
  }

  async function initializeChat() {
    const storedChat = localStorage.getItem("currentChat");

    if (storedChat) {
      setCurrentChat(JSON.parse(storedChat));
      return;
    }

    const { data = {} } = await axios({
      method: "post",
      url: "/system/ai/chats",
    });

    setCurrentChat(data);
    localStorage.setItem("currentChat", JSON.stringify(data));
  }

  async function completeChat(nextMessages: ChatMessage[]) {
    if (!currentChat) return;

    await axios({
      method: "post",
      url: `/system/ai/chats/${currentChat.id}/complete`,
      data: {
        ai_chat: {
          messages: nextMessages,
          field_name: normalizedFieldName(),
          mode: inferMode(),
        },
      },
    });
  }

  function startNewChat() {
    localStorage.removeItem("currentChat");
    localStorage.removeItem("chatMessages");
    setCurrentChat(null);
    setMessages([]);
    setCurrentCompletion(null);
    setErrorMessage(null);
    setCurrentAiText("");
    setIsCompleting(false);
    initializeChat();

    setTimeout(() => {
      inputAiTextareaRef.current?.focus();
    }, 0);
  }

  async function handleSubmit(e: FormEvent | MouseEvent) {
    e.preventDefault();
    if (isCompleting || currentAiText.trim().length < 3 || !currentChat) return;

    const nextMessages = [
      ...messages,
      { role: "user" as const, content: currentAiText.trim() },
    ];

    persistMessages(nextMessages);
    setCurrentAiText("");
    setCurrentCompletion(null);
    setErrorMessage(null);
    setIsCompleting(true);

    try {
      await completeChat(nextMessages);
    } catch (_error) {
      setErrorMessage("La generation a echoue.");
      setCurrentCompletion(null);
      setIsCompleting(false);
    }
  }

  function handleClose(e: any) {
    setIsLocalOpen(false);
    setTimeout(() => {
      onClose(e);
    }, 300);
  }

  useEffect(() => {
    portalRoot.current = document.createElement("div");
    portalRoot.current.id = "drawer-portal";
    document.body.appendChild(portalRoot.current);

    return () => {
      if (portalRoot.current) {
        document.body.removeChild(portalRoot.current);
      }
    };
  }, []);

  useEffect(() => {
    initializeChat();

    const savedMessages = localStorage.getItem("chatMessages");
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }

    setTimeout(() => {
      inputAiTextareaRef.current?.focus();
    }, 0);
  }, []);

  useEffect(() => {
    if (!currentChat) return;

    consumerRef.current = createConsumer();
    subscriptionRef.current = consumerRef.current.subscriptions.create(
      {
        channel: "System::Ai::ChatChannel",
        chat_id: currentChat.id,
      },
      {
        received(data: {
          type: string;
          content?: string;
          message?: ChatMessage;
          error?: string;
        }) {
          if (data.type === "content_delta") {
            setCurrentCompletion({
              role: "assistant",
              content: data.content || "",
            });
            return;
          }

          if (data.type === "message_completed" && data.message) {
            const nextMessages = [...messagesRef.current, data.message];
            persistMessages(nextMessages);
            setCurrentCompletion(null);
            setIsCompleting(false);
            return;
          }

          if (data.type === "message_error") {
            setErrorMessage(data.error || "La generation a echoue.");
            setCurrentCompletion(null);
            setIsCompleting(false);
          }
        },
      },
    );

    return () => {
      subscriptionRef.current?.unsubscribe();
      consumerRef.current?.disconnect();
      subscriptionRef.current = null;
      consumerRef.current = null;
    };
  }, [currentChat]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [messages, currentCompletion]);

  useEffect(() => {
    setTimeout(() => {
      setIsLocalOpen(true);
    }, 50);

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose(event);
      }
    };

    window.addEventListener("keydown", handleEscKey);
    window.dispatchEvent(new CustomEvent("drawer:opened"));

    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  const transitionClasses = "transition-transform duration-300";

  const drawerContent = (
    <>
      <button
        type="button"
        onClick={handleClose}
        style={{
          opacity: isLocalOpen ? 0.8 : 0,
        }}
        className={`fixed top-0 left-0 right-0 bottom-0 bg-gray-700/80 z-[99] ${transitionClasses}`}
      ></button>
      <div
        style={{
          transform: `translateX(${isLocalOpen ? "0" : "100%"})`,
        }}
        className={`fixed w-[700px] top-0 right-0 bottom-0 bg-white z-[100] flex flex-col divide-y divide-gray-300 border-l border-gray-300 ${transitionClasses}`}
      >
        <div className="flex-shrink-0 flex items-center justify-between py-3 px-6">
          <h2 className="font-medium text-xl tracking-tight">
            Rediger un nouvel article
          </h2>

          <div>
            <button
              type="button"
              className="bg-default-500 text-white px-3 py-1 rounded"
              onClick={startNewChat}
            >
              Reinitialiser
            </button>
          </div>
        </div>
        <div className="flex-shrink overflow-y-scroll h-full space-y-6 p-6 bg-default-50/50">
          {messages.map((message, index) => (
            <Message
              key={`message-${index}`}
              message={message}
              onSelectText={onSelectAIText}
              isCompleting={isCompleting}
            />
          ))}
          {currentCompletion && (
            <Message
              message={currentCompletion}
              onSelectText={onSelectAIText}
              isCompleting={isCompleting}
            />
          )}
          {isCompleting && !currentCompletion && (
            <div className="relative rounded-lg bg-white px-6 py-5 text-gray-500 ring-2 ring-default-200 shadow-lg shadow-default-50">
              <div className="absolute left-5 top-0 rounded-b bg-default-50 px-1 py-0.5 text-[10px] text-default-400 ring-1 ring-default-200/50">
                Assistant
              </div>
              <div className="flex items-center">
                <LoaderCircle className="h-4 w-4 animate-spin text-default-500" />
              </div>
            </div>
          )}
          {errorMessage && (
            <div className="rounded-lg bg-red-50 p-6 text-red-700 ring-1 ring-red-200 shadow-lg shadow-red-50">
              <div className="mb-3 inline-flex rounded-full bg-white px-2 py-1 text-[10px] text-red-500 ring-1 ring-red-200">
                Erreur
              </div>
              <p>{errorMessage}</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex-shrink-0 bg-white py-3 px-6 relative">
          <TextareaAutosize
            value={currentAiText}
            onChange={(e) => setCurrentAiText(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                currentAiText.trim().length >= 3
              ) {
                handleSubmit(e);
              }
            }}
            minRows={2}
            className={className}
            ref={inputAiTextareaRef}
          />
          <button
            type="button"
            className={`${currentAiText.trim().length < 3 || isCompleting ? "opacity-50 pointer-events-none" : ""} ${buttonClasses} absolute bottom-7 right-8`}
            onClick={handleSubmit}
          >
            Envoyer
          </button>
        </div>
      </div>
    </>
  );

  if (!portalRoot.current) return null;

  return createPortal(drawerContent, portalRoot.current);
};

export default DrawerPortal;
